/**
 * A window of days in one field, picked by name or from the grid.
 *
 * The dashboard used to offer six tabs, which is a row of the page spent on a
 * question with more than six answers: "the first week of August" was not
 * reachable, and the field could not say which dates it was showing. This is the
 * replacement — one control that reads its own range, opening the presets a
 * merchant reaches for by name beside a grid for everything else.
 *
 * The presets are props rather than keys: the words belong to the screen that
 * knows what its periods mean, and the dates arrive already resolved in the
 * shop's own calendar, so this component never asks what "this month" is.
 *
 * Two presses pick a custom window — first day, then last — and the days between
 * are tinted while it is being chosen. A preset is one press and closes. There is
 * no Apply button, because both gestures are complete without one.
 */

import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { CalendarDays } from 'lucide-react-native';

import { Calendar, monthOf, parseIsoDate, todayParts, type CalendarMonth } from './calendar';
import { Row } from './layout';
import { Icon } from './ui/icon';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from './ui/popover';
import { Text } from './ui/text';
import { useLocale } from '../providers/locale';
import { cn } from '../lib/utils';

/**
 * One of the windows a merchant reaches for by name.
 *
 * An opaque value and a label, with no dates: what "this month" resolves to is
 * the shop's business and is computed in the shop's own timezone on the server,
 * so a preset here is a name to offer back rather than a pair of days to send.
 */
export interface DateRangePreset {
  /** Handed back on press; the caller decides what it means. */
  value: string;
  /** What they read, in their own language. The caller owns the words. */
  label: string;
}

export interface DateRangeFieldProps {
  /** The window on screen: its first day, `YYYY-MM-DD`. */
  from: string;
  /** Its last day, inclusive. */
  to: string;
  /** The named windows to offer, in the order they should be read. */
  presets?: readonly DateRangePreset[];
  /** Which preset the window is, when it is one — so the field can say its name. */
  activePreset?: string | null;
  /** A preset was pressed. The caller resolves it, because the server does. */
  onPreset: (value: string) => void;
  /** Two days were picked. */
  onRange: (range: { from: string; to: string }) => void;
  /** The control's accessible name, e.g. "Reporting period". */
  label: string;
  disabled?: boolean;
}

/**
 * The window, as one field.
 *
 * @param props The current range, the presets, and what to do when it changes.
 * @returns The field and its popover.
 */
export function DateRangeField({
  from,
  to,
  presets = [],
  activePreset = null,
  onPreset,
  onRange,
  label,
  disabled,
}: DateRangeFieldProps) {
  const { t, locale } = useLocale();
  /**
   * The first day of a custom window, held while the merchant picks the last.
   *
   * Cleared when the popover closes, so a half-chosen range cannot survive into
   * the next time the field is opened and turn the next press into a surprise.
   */
  const [pending, setPending] = useState<string | null>(null);
  const [month, setMonth] = useState<CalendarMonth>(() => monthOf(from) ?? todayParts());

  const day = (iso: string) => {
    const parts = parseIsoDate(iso);
    if (!parts) return '—';
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(parts.year, parts.month, parts.day));
  };

  const chosen = presets.find((preset) => preset.value === activePreset);


  const pressDay = (iso: string) => {
    if (!pending) {
      setPending(iso);
      return;
    }
    onRange(pending <= iso ? { from: pending, to: iso } : { from: iso, to: pending });
    // The popover stays open: the two ends are now marked on the grid, and Done
    // is the press that closes it. Closing on the second day would take the
    // evidence away in the same motion as the choice.
    setPending(null);
  };

  return (
    <Popover
      onOpenChange={(next) => {
        // A half-drawn range does not survive the popover closing, so the next
        // press cannot complete yesterday's gesture.
        if (!next) setPending(null);
      }}>
      <PopoverTrigger asChild>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={label}
          disabled={disabled}
          className={cn(
            'bg-background border-input min-h-12 flex-row items-center justify-between gap-3 rounded-md border px-3',
            disabled && 'bg-muted opacity-60'
          )}>
          {/* Always the dates, never the preset's name: the field is what the
              page is showing, and "This month" is a name for a window rather
              than the window. Which preset it came from is marked in the
              popover, where choosing happens. */}
          <Text className="text-foreground text-sm font-medium">
            {`${day(from)} – ${day(to)}`}
          </Text>
          <Icon as={CalendarDays} size={16} className="text-foreground" />
        </Pressable>
      </PopoverTrigger>

      {/* 472 is the two columns added: 172 for the presets beside 300 for the
          grid, whose own 252 of cells sit inside it. Stated as one number because
          this element is what the grid is laid out in — a grid with no room wraps
          its weeks at eight days, which is what a contents-sized popover did. */}
      <PopoverContent align="start" className="w-[472px] p-0">
        <View className="flex-row">
          {presets.length > 0 ? (
            <View className="border-border w-[172px] border-r p-2">
              {presets.map((preset) => {
                const active = preset.value === activePreset;
                return (
                  <PopoverClose key={preset.value} asChild>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={preset.label}
                      onPress={() => onPreset(preset.value)}
                      className={cn(
                        'rounded-md px-3 py-2',
                        active ? 'bg-muted' : 'active:bg-accent'
                      )}>
                      <Text className={cn('text-sm', active && 'font-semibold')}>{preset.label}</Text>
                    </Pressable>
                  </PopoverClose>
                );
              })}
            </View>
          ) : null}

          <View className="w-[300px] p-3">
            <Calendar
              value={pending ?? from}
              // While the first day is held the grid shows that alone; the span
              // appears once there is a second end to draw it to.
              endValue={pending ? undefined : to}
              month={month}
              onMonthChange={setMonth}
              onChange={pressDay}
            />
            <Row spread align="center" className="border-border mt-2 gap-3 border-t pt-2">
              <Text className="text-muted-foreground text-xs">
                {pending
                  ? t('common.pickRangeEnd')
                  : chosen
                    ? `${day(from)} – ${day(to)}`
                    : t('common.pickRangeStart')}
              </Text>
              <PopoverClose asChild>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('common.done')}
                  className="bg-primary h-9 items-center justify-center rounded-md px-3 active:opacity-90">
                  <Text className="text-primary-foreground text-sm font-medium">
                    {t('common.done')}
                  </Text>
                </Pressable>
              </PopoverClose>
            </Row>
          </View>
        </View>
      </PopoverContent>
    </Popover>
  );
}
